<template>
  <PopupBackground @click="$emit('close')">
    <div class="duplicate-console-popup smooth-shadow">
      <!-- Header -->
      <div class="header">
        <div class="title">
          <h3>{{ layerToEdit ? "Edit" : "Add" }} Event Totals Analysis</h3>
          <p>Analyze events happening at your entities</p>
        </div>
      </div>
      <div v-if="isLoading" class="contents">
        <LoadingBar />
      </div>
      <div v-else class="contents">
        <label>Layer Name</label>
        <input
          type="text"
          v-model="layerName"
          placeholder="Enter the layer name"
          ref="inputName"
        />
        <label>Entity</label>
        <DropDown
          v-model="selectedEntity"
          placeholder="Choose an entity"
          :options="entityOptions"
          class="mb-10"
        />
        <div class="row">
          <div class="column">
            <label>Event Name</label>
            <SearchableDropDown
              v-model="selectedEvent"
              placeholder="Event Name"
              :options="eventOptions"
              class="mb-10"
            />
          </div>
          <div class="column">
            <label>Location column</label>
            <DropDown
              v-model="referenceColumn"
              placeholder="Choose a column"
              :options="referenceOptions"
              class="mb-10"
            />
          </div>
        </div>
        <!-- <div class="flex-row-align-center pad-top-10 pad-bottom-10">
          <label>Use Entity Property</label>
          <expand />
          <ToggleSwitch v-model="showProperty" />
        </div> -->
        <h4 style="font-weight: bold;" class="pad-bottom-10">
          Visualization property
        </h4>
        <transition name="slide">
          <div class="row">
            <div class="column">
              <label>Property</label>
              <SearchableDropDown
                v-model="selectedProperty"
                placeholder="Choose a property"
                :options="getColumns"
                class="mb-10"
                :dropup="true"
              />
            </div>
            <div
              v-if="selectedProperty && selectedProperty.aggs"
              class="column"
            >
              <label>Aggregation</label>
              <SearchableDropDown
                v-model="selectedAgg"
                placeholder="Choose Aggregation"
                :options="selectedProperty && selectedProperty.aggs"
                class="mb-10"
                :dropup="true"
              />
            </div>
          </div>
        </transition>
        <!-- <transition name="slide">
          <div
            v-if="this.selectedEvent && !this.selectedEvent.static_entity_col"
          >
            <label>Radius Filter</label>
            <div class="radiusPicker">
              <RangePicker v-model="radius" />
              <input
                type="text"
                v-model="radius"
                placeholder="Radius"
                ref="inputName"
              />
            </div>
          </div>
        </transition> -->
      </div>
      <!-- Footer -->
      <div class="footer">
        <expand />
        <Button
          v-if="!isRequestInProgress"
          type="secondary"
          :text="'Cancel'"
          :icon="'clear'"
          @click="handleBack"
        />
        <spinner v-if="isRequestInProgress" />
        <Button
          v-else
          type="primary"
          :text="layerToEdit ? 'Save Layer' : 'Add Layer'"
          icon="check"
          @click="addLayerToStore"
          :disabled="isButtonDisabled"
        />
      </div>
    </div>
  </PopupBackground>
</template>

<script>
import PopupBackground from "@/components/modals/PopupBackground";
import Button from "@/components/global/views/Button";
import RangePicker from "@/components/global/views/RangePicker";
import SearchableDropDown from "@/components/metrics/views/SearchableDropDown";
import { EventTotals } from "@/models/StaticEntityAnalysis/layers";
import ToggleSwitch from "@/components/global/views/toggleSwitch";
import { mapActions, mapGetters, mapState } from "vuex";
import { SlideYUpTransition } from "vue2-transitions";

export default {
  components: {
    PopupBackground,
    Button,
    RangePicker,
    SearchableDropDown,
    ToggleSwitch,
    SlideYUpTransition,
  },
  props: {
    layerToEdit: Object,
  },
  data() {
    return {
      isRequestInProgress: false,
      layerName: "",
      selectedEntity: null,
      selectedEntityDetails: null,
      selectedEvent: null,
      showRadiusFilter: false,
      hasRadiusFilter: true,
      referenceColumn: null,
      selectedProperty: null,
      selectedAgg: null,
      showProperty: false,
      radius: "50",
    };
  },
  computed: {
    ...mapGetters("Entities", ["getEntities", "getEntityEvents"]),
    ...mapState("Entities", ["entityEvents"]),
    ...mapGetters("StaticEntity", ["getCurrentStaticEntity"]),
    ...mapGetters("Datasources", ["getDataSources", "getDataSourceDetails"]),
    entityOptions() {
      return this.getEntityEvents
        .filter((entity) => !!entity.static_analysis_meta)
        .map((entity) => ({
          id: entity.id,
          name: entity.name,
        }));
      // return this.getEntities
      //   .filter(entity => !!entity.meta && !!entity.meta.lookup_view)
      //   .map(entity => ({
      //     id: entity.id,
      //     name: entity.name,
      //     event_col_name:
      //       entity.meta && entity.meta.lookup_view
      //         ? entity.meta.lookup_view.event_col_name
      //         : "No event col",
      //   }));
    },
    eventOptions() {
      if (this.selectedEntity) {
        return this.entityEvents[
          this.selectedEntity.id
        ].static_analysis_meta.events.map((event) => ({
          id: event.event_name + event.static_entity_col,
          name: event.event_name,
          ...event,
        }));

        // find(
        //   entity => entity.id === this.selectedEntity.id
        // ).meta.lookup_view.event_col_options;
        // return this.getEntities.find(
        //   entity => entity.id === this.selectedEntity.id
        // ).meta.lookup_view.event_col_options;
      } else {
        return null;
      }
    },
    referenceOptions() {
      if (!this.selectedEntity) return;
      const currentEntityEvents = this.entityEvents[this.selectedEntity.id];
      if (
        currentEntityEvents &&
        currentEntityEvents.static_analysis_meta &&
        currentEntityEvents.static_analysis_meta.entity_refs
      ) {
        const references = currentEntityEvents.static_analysis_meta.entity_refs.filter(
          (item) => item.ref_entity_id === this.getCurrentStaticEntity
        );
        if (references && references.length > 0) {
          return references.map((item) => ({
            ...item,
            id: item.base_entity_column,
            name: item.ref_entity_name,
          }));
        }
      }
      return [];
    },
    isButtonDisabled() {
      return !(
        this.layerName &&
        this.selectedEntity &&
        this.selectedEvent &&
        this.referenceColumn &&
        this.selectedProperty &&
        this.selectedAgg
      );
    },
    isLoading() {
      return !(
        this.getEntities &&
        this.getEntities.length > 0 &&
        this.getDataSources &&
        this.getDataSources.length > 0
      );
    },
    getColumns() {
      // check if entity id and all the other data required to fetch the columns are present
      if (
        !(
          this.getEntities &&
          this.getEntities.length > 0 &&
          this.getDataSources &&
          this.selectedEntity
        )
      ) {
        return;
      }

      const columns = [];
      // get the details of the selected entity
      const selectedEntity = this.getEntities.find(
        (item) => item.id === this.selectedEntity.id
      );
      // this.selectedEntityDetails = selectedEntity;
      if (selectedEntity) {
        // get all the datasource details of the selected entity
        selectedEntity.datasources.map((datasource) => {
          // get all the selected columns of the entity from each of its datasource
          const dataSourceDetails = this.getDataSourceDetails(
            datasource.datasource_id
          );

          if (dataSourceDetails && dataSourceDetails.columns) {
            dataSourceDetails.columns.forEach((column) => {
              // add the columns only if it's a measure
              if (
                datasource.selected_cols.includes(column.name) &&
                column.is_measure
              ) {
                columns.push(column);
              }
              // check if the column is an entity id col, then push that
              else if (
                datasource.type === "base" &&
                column.name === selectedEntity.entity_id_col
              ) {
                columns.push({
                  ...column,
                  aggs: [
                    {
                      id: "count_unique",
                      name: "count_unique",
                    },
                    {
                      id: "count",
                      name: "count",
                    },
                  ],
                });
              }
            });
          }
        });
      }

      return [...columns];
    },
  },
  methods: {
    ...mapActions("Layers", ["addLayer", "updateLayer"]),
    ...mapActions("StaticEntity", ["fetchLayerData"]),
    ...mapActions("Entities", ["fetchEntities"]),
    ...mapActions("Datasources", ["fetchDataSources"]),
    async addLayerToStore() {
      // use property details only if the show Property is enabled
      const propertyDetails =
        this.selectedProperty && this.selectedAgg
          ? {
              property: this.selectedProperty,
              agg: this.selectedAgg,
            }
          : {};

      const newEventTotalsLayer = new EventTotals({
        id: this.layerToEdit ? this.layerToEdit.id : null,
        name: this.layerName,
        static_entity: [],
        entity: this.selectedEntity,
        event: this.selectedEvent,
        refColumn: this.referenceColumn,
        ...propertyDetails,
        // radius:
        //   this.selectedEvent && !this.selectedEvent.static_entity_col
        //     ? this.radius
        //     : undefined,
      });

      if (this.layerToEdit) {
        // Update the layer:
        this.updateLayer(newEventTotalsLayer);
        this.fetchLayerData({ layerId: this.layerToEdit.id });
      } else {
        // Create a new layer:
        const layerId = await this.addLayer(newEventTotalsLayer);
        this.fetchLayerData({ layerId });
      }
      this.$emit("close");
    },
    handleBack() {
      this.$emit("close");
      //this.$emit("openParent");
    },
    populatedForm() {
      this.layerName = this.layerToEdit.name;
      this.selectedEntity = this.layerToEdit.entity;
      // this.radius = this.layerToEdit.radius;
      this.selectedEvent = this.layerToEdit.event;
      this.selectedProperty = this.getColumns.find(
        (item) => item.id === this.layerToEdit.property.id
      );
      this.selectedAgg = this.layerToEdit.agg;
      this.referenceColumn = this.layerToEdit.refColumn;
    },
  },
  mounted() {
    if (!(this.getEntities && this.getEntities.length > 0)) {
      this.fetchEntities();
    }

    // fetch all data sources
    if (!(this.getDataSources && this.getDataSources.length > 0)) {
      this.fetchDataSources();
    }
  },
  watch: {
    layerToEdit: {
      immediate: true,
      handler(layer) {
        if (!this.isLoading && layer) {
          this.populatedForm();
        }
      },
    },
    isLoading: {
      immediate: true,
      handler(isLoading) {
        if (!isLoading && this.layerToEdit) {
          this.populatedForm();
        }
      },
    },
    selectedEntity() {
      this.selectedEvent = null;
    },
    selectedEntityDetails(value) {
      if (this.layerToEdit && this.layerToEdit.property && this.layerToEdit.agg)
        return;

      const baseDs = this.selectedEntityDetails.datasources.find(
        (item) => item.type === "base"
      );
      const column_id = `${baseDs.datasource_id}__${baseDs.entity_id_col}`;
      this.selectedProperty = {
        id: column_id,
        name: baseDs.entity_id_col,
        aggs: [
          {
            id: "count",
            name: "count",
          },
          {
            id: "count_unique",
            name: "count unique",
          },
        ],
      };
      this.selectedAgg = {
        id: "count",
        name: "count",
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.duplicate-console-popup {
  display: flex;
  flex-direction: column;
  background: var(--color-secondary);
  border-radius: 5px;
  width: 80%;
  max-width: 450px;

  .header,
  .footer,
  .contents {
    padding: 10px;
  }

  .header {
    padding: 20px 15px 10px 15px;
    display: flex;
    align-items: center;

    .title {
      display: flex;
      flex-direction: column;
      // align-items: center;

      i {
        margin-right: 10px;
      }

      h3 {
        font-weight: bold;
      }

      p {
        margin: 0;
        opacity: 0.8;
        padding: 10px 0 0 0;
      }
    }
  }

  .contents {
    display: flex;
    flex-direction: column;
    padding: 15px;

    label {
      margin: 0 0 5px 0;
    }
    input {
      margin: 0 0 10px 0;
    }
    .mb-10 {
      margin-bottom: 10px;
    }

    .radiusPicker {
      display: flex;
      align-items: center;

      input[type="text"] {
        margin-left: 10px;
        max-width: 50px;
      }
    }
  }

  .row {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr 1fr;
    min-height: 0; /* NEW */
    min-width: 0; /* NEW; needed for Firefox */

    .column {
      display: flex;
      flex-direction: column;
      // flex-grow: 1;
      // flex-basis: 0;
      min-width: 0;

      &:first-child {
        margin-right: 10px;
      }
    }
  }

  .footer {
    background-color: var(--color-default-background);
    display: flex;
    align-items: center;
    border-radius: 0 0 5px 5px;
  }
}
</style>
